import { Eye, EyeOff } from 'lucide-react'
import { type ReactNode, useState } from 'react'

/** `<div className="field">` + input con el mostrar/ocultar de siempre —
 *  un solo lugar para el patrón, en vez de repetir el botón del ojo y su
 *  estado en cada formulario que pide una contraseña o un secreto.
 *
 *  La visibilidad es propia por default (cada campo la maneja sola), pero
 *  se puede controlar desde afuera con `visible`/`onVisibleChange` — para
 *  "contraseña nueva" + "confirmar contraseña nueva": son el mismo valor
 *  escrito dos veces, así que un solo ojo debería descubrir las dos a la
 *  vez en vez de que haya que tildar cada una por separado. */
export function PasswordField({
  id,
  name,
  label,
  value,
  onChange,
  autoComplete,
  autoFocus,
  required,
  minLength,
  hint,
  visible: controlledVisible,
  onVisibleChange,
  labelAction,
}: {
  id: string
  /** Los gestores de contraseñas heurísticos (los que no confían sólo en
   *  `autocomplete`) matchean campos por `name` — sin él degradan el
   *  guardado/autocompletado. Por defecto usa `id`. */
  name?: string
  label: string
  value: string
  onChange: (value: string) => void
  autoComplete?: string
  autoFocus?: boolean
  required?: boolean
  minLength?: number
  hint?: ReactNode
  visible?: boolean
  onVisibleChange?: (visible: boolean) => void
  /** Algo para mostrar a la derecha del label, en la misma fila — un link
   *  de "¿Olvidaste tu contraseña?", por ejemplo. */
  labelAction?: ReactNode
}) {
  const [ownVisible, setOwnVisible] = useState(false)
  const visible = controlledVisible ?? ownVisible
  const setVisible = onVisibleChange ?? setOwnVisible

  return (
    <div className="field">
      {labelAction ? (
        <div className="field__label-row">
          <label htmlFor={id}>{label}</label>
          {labelAction}
        </div>
      ) : (
        <label htmlFor={id}>{label}</label>
      )}
      <div className="password-field">
        <input
          id={id}
          name={name ?? id}
          type={visible ? 'text' : 'password'}
          autoComplete={autoComplete}
          // Sólo importa cuando `visible` pasa esto a type="text" — un
          // password real ya viene sin corrector por el propio navegador,
          // pero al "mostrar" la contraseña/secret quedaba expuesto a
          // spellcheck/autocorrect como cualquier input de texto.
          spellCheck={false}
          autoCorrect="off"
          autoCapitalize="off"
          autoFocus={autoFocus}
          required={required}
          minLength={minLength}
          value={value}
          onChange={(event) => onChange(event.target.value)}
        />
        <button
          type="button"
          className="password-field__toggle"
          onClick={() => setVisible(!visible)}
          aria-label={visible ? 'Ocultar contraseña' : 'Mostrar contraseña'}
          aria-pressed={visible}
        >
          {visible ? <EyeOff size={16} aria-hidden="true" /> : <Eye size={16} aria-hidden="true" />}
        </button>
      </div>
      {hint && <p className="muted field__hint">{hint}</p>}
    </div>
  )
}
